import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Send, Wand2, Loader2, Plus, MessageSquare, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Coach() {
  const [tab, setTab] = useState("chat");
  return (
    <div className="space-y-6 pb-24 md:pb-8" data-testid="coach-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Powered by SPC
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Your AI Coach</h1>
        <p className="text-white mt-2">Get personal motivation, advice, and a custom habit plan for any goal.</p>
      </div>

      <div className="flex gap-2">
        <TabBtn active={tab === "chat"} onClick={() => setTab("chat")} icon={<MessageSquare className="w-4 h-4" />} label="Coach Chat" testid="coach-tab-chat" />
        <TabBtn active={tab === "plan"} onClick={() => setTab("plan")} icon={<Wand2 className="w-4 h-4" />} label="Plan Builder" testid="coach-tab-plan" />
      </div>

      {tab === "chat" ? <CoachChat /> : <PlanBuilder />}
    </div>
  );
}

const TabBtn = ({ active, onClick, icon, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2 px-4 h-11 rounded-xl font-medium border transition-colors ${
      active ? "bg-white text-black border-white" : "border-[#242417] text-white hover:bg-white/5"
    }`}
  >
    {icon} {label}
  </button>
);

const SUGGESTIONS = [
  "I keep breaking my streak. Help!",
  "How do I make my habits stick?",
  "Motivate me for today",
  "I have no time in the mornings",
];

function CoachChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/ai/coach/history").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/coach", { message: msg });
      setMessages((m) => [...m, { role: "coach", text: data.reply }]);
    } catch (e) {
      toast.error("Coach is unavailable right now");
      setMessages((m) => [...m, { role: "coach", text: "Sorry, I couldn't respond just now. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl flex flex-col h-[65vh]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="coach-messages">
        {messages.length === 0 && !loading && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center mb-4 glow-emerald">
              <Sparkles className="w-7 h-7 text-black" />
            </div>
            <h3 className="font-display text-lg font-bold mb-1">Say hi to your coach</h3>
            <p className="text-white text-sm mb-5 max-w-sm">Ask anything about building habits, staying consistent, or beating procrastination.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid={`coach-suggestion`} className="text-xs px-3 py-1.5 rounded-full border border-[#33331f] text-white hover:border-emerald-500/50 hover:text-emerald-400 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap ${
              m.role === "user" ? "bg-gradient-to-r from-emerald-500 to-teal-400 text-black rounded-br-sm" : "bg-white/5 text-white rounded-bl-sm border border-[#242417]"
            }`}>
              {m.text}
            </div>
          </motion.div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-4 py-3 rounded-2xl bg-white/5 border border-[#242417] text-white text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Coach is thinking…
            </div>
          </div>
        )}
      </div>
      <div className="p-3 border-t border-[#242417] flex gap-2">
        <input
          data-testid="coach-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Message your coach…"
          className="flex-1 h-11 px-4 rounded-xl bg-[#0D0F17] border border-[#242417] text-white outline-none focus:border-emerald-400"
        />
        <button data-testid="coach-send-button" onClick={() => send()} disabled={loading} className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center text-black disabled:opacity-50">
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function PlanBuilder() {
  const [goal, setGoal] = useState("");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState({});      // index -> true once added individually
  const [addingIdx, setAddingIdx] = useState(null);

  const generate = async () => {
    if (!goal.trim() || loading) return;
    setLoading(true);
    setPlan(null);
    setAdded({});
    try {
      const { data } = await api.post("/ai/plan", { goal });
      setPlan(data);
    } catch (e) {
      toast.error("Couldn't generate a plan. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const addOne = async (h, i) => {
    if (added[i] || addingIdx !== null) return;
    setAddingIdx(i);
    try {
      await api.post("/habits/bulk", { habits: [h] });
      setAdded((a) => ({ ...a, [i]: true }));
      toast.success(`Added "${h.title}" to your dashboard`);
    } catch (e) {
      toast.error("Failed to add habit");
    } finally {
      setAddingIdx(null);
    }
  };

  const addAll = async () => {
    const remaining = (plan.habits || []).filter((_, i) => !added[i]);
    if (!remaining.length) return;
    setAdding(true);
    try {
      await api.post("/habits/bulk", { habits: remaining });
      toast.success(`Added ${remaining.length} habit${remaining.length > 1 ? "s" : ""} to your dashboard!`);
      setPlan(null);
      setGoal("");
      setAdded({});
    } catch (e) {
      toast.error("Failed to add habits");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="p-5 sm:p-6 rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl">
        <label className="block text-xs font-medium uppercase tracking-wider text-white mb-2">What's your goal?</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="plan-goal-input"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && generate()}
            placeholder="e.g. Get fit, sleep better, read more…"
            className="flex-1 h-12 px-4 rounded-xl bg-[#0D0F17] border border-[#242417] text-white outline-none focus:border-emerald-400"
          />
          <button data-testid="plan-generate-button" onClick={generate} disabled={loading} className="h-12 px-5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
            Build Plan
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {["Get fit", "Sleep better", "Reduce stress", "Read more", "Save money"].map((g) => (
            <button key={g} onClick={() => setGoal(g)} className="text-xs px-3 py-1.5 rounded-full border border-[#33331f] text-white hover:border-emerald-500/50 hover:text-emerald-400 transition-colors">
              {g}
            </button>
          ))}
        </div>
      </div>

      {plan && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="p-5 sm:p-6 rounded-2xl border border-emerald-500/30 bg-[#12141C] shadow-xl" data-testid="plan-result">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold">{plan.title || "Your Plan"}</h3>
            <button data-testid="plan-add-all-button" onClick={addAll} disabled={adding || !plan.habits?.length || plan.habits.every((_, i) => added[i])} className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center gap-2 disabled:opacity-60">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add all
            </button>
          </div>
          <div className="space-y-3">
            {plan.habits?.map((h, i) => (
              <div key={i} data-testid={`plan-habit-${i}`} className="p-4 rounded-xl border border-[#242417] bg-white/[0.02] flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{h.title}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-white border border-[#242417]">{h.category}</span>
                    {h.type === "countable" && <span className="text-xs text-emerald-400 font-mono-stat">{h.target} {h.unit}</span>}
                    {h.reminder && <span className="text-xs text-white">⏰ {h.reminder}</span>}
                  </div>
                  {h.description && <p className="text-sm text-white mt-1">{h.description}</p>}
                </div>
                {added[i] ? (
                  <span data-testid={`plan-habit-added-${i}`} className="shrink-0 h-9 px-3 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm font-semibold flex items-center gap-1.5">
                    <Check className="w-4 h-4" /> Added
                  </span>
                ) : (
                  <button
                    data-testid={`plan-habit-add-${i}`}
                    onClick={() => addOne(h, i)}
                    disabled={addingIdx !== null}
                    title="Add this habit"
                    className="shrink-0 h-9 px-3 rounded-lg border border-emerald-500/40 text-emerald-300 text-sm font-semibold flex items-center gap-1.5 hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
                  >
                    {addingIdx === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
                  </button>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
